import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../../api/client';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';

interface Survey {
  id: string;
  title: string;
  description?: string;
  status: string;
  responseCount: number;
  targetCount?: number;
  createdByName: string;
  createdAt: string;
}

interface Question {
  id: string;
  type: 'single' | 'multiple' | 'text' | 'scale' | 'select';
  text: string;
  options?: string[];
  order: number;
}

interface QuestionResult {
  questionId: string;
  type: string;
  text: string;
  totalResponses: number;
  optionCounts?: Record<string, number>;
  textResponses?: string[];
  averageScore?: number;
  scaleDistribution?: Record<number, number>;
}

export default function SurveyResults() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [results, setResults] = useState<QuestionResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [surveyData, questionsData, resultsData] = await Promise.all([
        api.get<Survey>(`/api/surveys/${id}`),
        api.get<{ items: Question[] }>(`/api/surveys/${id}/questions`),
        api.get<{ items: QuestionResult[] }>(`/api/surveys/${id}/results`),
      ]);
      setSurvey(surveyData);
      setQuestions(questionsData.items || []);
      setResults(resultsData.items || []);
    } catch {
      navigate('/surveys');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await api.get<{ csv: string }>(`/api/surveys/${id}/export`);
      const blob = new Blob([response.csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `survey_${id}_results.csv`;
      link.click();
    } catch {
      toast.error('エクスポートに失敗しました');
    } finally {
      setExporting(false);
    }
  };

  const getResultForQuestion = (questionId: string) => {
    return results.find((r) => r.questionId === questionId);
  };

  const getPercentage = (count: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((count / total) * 100);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">アンケートが見つかりません</p>
        <Link to="/surveys" className="text-blue-600 hover:underline mt-4 inline-block">
          一覧に戻る
        </Link>
      </div>
    );
  }

  const responseRate = survey.targetCount
    ? Math.round((survey.responseCount / survey.targetCount) * 100)
    : null;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(`/surveys/${id}`)}>
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          アンケートに戻る
        </Button>
        <Button variant="secondary" onClick={handleExportCSV} disabled={exporting}>
          {exporting ? 'エクスポート中...' : 'CSV エクスポート'}
        </Button>
      </div>

      {/* サマリー */}
      <Card>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{survey.title} - 結果</h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{survey.responseCount}</p>
            <p className="text-sm text-gray-500">回答数</p>
          </div>
          {survey.targetCount && (
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{responseRate}%</p>
              <p className="text-sm text-gray-500">回答率</p>
            </div>
          )}
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 text-center">
            <p className="text-3xl font-bold text-purple-600">{questions.length}</p>
            <p className="text-sm text-gray-500">質問数</p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
            <Badge variant={survey.status === 'published' ? 'success' : survey.status === 'closed' ? 'danger' : 'default'}>
              {survey.status === 'published' ? '公開中' : survey.status === 'closed' ? '終了' : '下書き'}
            </Badge>
            <p className="text-sm text-gray-500 mt-1">ステータス</p>
          </div>
        </div>
      </Card>

      {/* 質問別結果 */}
      {questions.sort((a, b) => a.order - b.order).map((question, idx) => {
        const result = getResultForQuestion(question.id);
        if (!result) return null;

        return (
          <Card key={question.id}>
            <div className="space-y-4">
              <div className="flex items-start gap-2">
                <span className="text-lg font-bold text-blue-600">{idx + 1}.</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="default">
                      {question.type === 'single' ? '単一選択' : question.type === 'multiple' ? '複数選択' : question.type === 'text' ? 'テキスト' : question.type === 'scale' ? '評価' : 'セレクト'}
                    </Badge>
                    <span className="text-sm text-gray-500">{result.totalResponses}件の回答</span>
                  </div>
                  <p className="font-medium text-gray-900 dark:text-white">{question.text}</p>
                </div>
              </div>

              {/* 選択肢の集計結果 */}
              {(question.type === 'single' || question.type === 'multiple' || question.type === 'select') && result.optionCounts && (
                <div className="space-y-3">
                  {question.options?.map((option) => {
                    const count = result.optionCounts?.[option] || 0;
                    const percentage = getPercentage(count, result.totalResponses);
                    return (
                      <div key={option}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-700 dark:text-gray-300">{option}</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {count}件 ({percentage}%)
                          </span>
                        </div>
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* 評価スケールの集計結果 */}
              {question.type === 'scale' && (
                <div className="space-y-4">
                  {result.averageScore !== undefined && (
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-500">平均スコア:</span>
                      <span className="text-2xl font-bold text-blue-600">{result.averageScore.toFixed(1)}</span>
                    </div>
                  )}
                  {result.scaleDistribution && (
                    <div className="flex items-end gap-2 h-32">
                      {Object.entries(result.scaleDistribution)
                        .sort(([a], [b]) => parseInt(a) - parseInt(b))
                        .map(([score, count]) => {
                          const maxCount = Math.max(...Object.values(result.scaleDistribution!));
                          const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                          return (
                            <div key={score} className="flex-1 flex flex-col items-center">
                              <div
                                className="w-full bg-blue-500 rounded-t transition-all duration-500"
                                style={{ height: `${height}%`, minHeight: count > 0 ? '8px' : '0' }}
                              />
                              <span className="text-xs text-gray-500 mt-1">{score}</span>
                              <span className="text-xs text-gray-400">{count}件</span>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}

              {/* テキスト回答一覧 */}
              {question.type === 'text' && result.textResponses && result.textResponses.length > 0 && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {result.textResponses.map((text, i) => (
                    <div
                      key={i}
                      className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300"
                    >
                      {text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        );
      })}

      {results.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📊</div>
            <p className="text-gray-500">まだ回答がありません</p>
          </div>
        </Card>
      )}
    </div>
  );
}
